import React, { Component } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { debounce, cloneDeep } from "lodash";
import { Search as SemanticSearch } from "semantic-ui-react";

/**
 * A simple wrapper for Semantic's Search that integrates with Nest's react/repository API.
 * This is a stateful component.
 */
@observer
class Search extends Component {
  promisesCount = 0;

  constructor(props) {
    super(props);

    const { debounceOptions } = props;
    const { timeout, ...remainingDebounceOptions } = debounceOptions;

    this.doSearch = this.doSearch.bind(this);
    this.doSearch = debounce(this.doSearch, timeout, remainingDebounceOptions);

    this.state = {
      results: [],
      loading: false,
    };
  }

  onSearchChange(event) {
    const { value } = event.target;

    const { minCharacters, onSearchChange } = this.props;

    onSearchChange(event);

    if (!(value && value.length >= minCharacters)) {
      this.setState({ loading: false });

      return;
    }

    this.setState({ loading: true });
    this.doSearch();
  }

  onResultSelect(event, data) {
    const { results } = this.state;
    const { onResultSelect } = this.props;

    // find the actual result with full properties, since selected result only has a 'title' prop.
    // Only array, no categories
    if (Array.isArray(results)) {
      for (let i = 0; i < results.length; i += 1) {
        const result = results[i];

        if (data.result.title === result.title) {
          onResultSelect(event, { ...data, result });

          return;
        }
      }
    } else {
      // Object, with categories
      // These are the keys
      const categories = Object.keys(results);

      // Go through the category keys
      for (let i = 0; i < categories.length; i += 1) {
        // The results inside each category is an array like the array with no categories
        for (let j = 0; j < results[categories[i]].results.length; j += 1) {
          const result = results[categories[i]].results[j];

          if (data.result.title === result.title) {
            onResultSelect(event, { ...data, result });

            return;
          }
        }
      }
    }

    throw new Error(`Could not find result in results list for title: ${data.result.title}`);
  }

  doSearch() {
    const { asyncSearchCallback, value } = this.props;

    // Workaround to overcome the fact that Promises can't be aborted.
    // We won't set the search result unless the current search is the last one. This helps to avoid
    // the overlap of results on top of each other.
    const promiseNumber = this.promisesCount + 1;
    this.promisesCount = promiseNumber;

    asyncSearchCallback(value).then(results => {
      // only execute if this is the last async call;
      if (promiseNumber < this.promisesCount) {
        return;
      }

      this.promisesCount = 0;
      this.setState({ results, loading: false });
    });
  }

  render() {
    const { value, asyncSearchCallback, debounceOptions, ...remainingProps } = this.props;
    const { results, loading } = this.state;

    let processedResults;
    let withCategory = false;

    // Semantic will spread any remaining property as dom attributes into the component.
    // For this reason we only need to extract the title from the object, which is the only required field.
    // Maybe in future we might want to support results of type string as well.
    if (Array.isArray(results)) {
      // Without category, the list is an array
      processedResults = results.map(result => ({ title: result.title }));
    } else {
      // With category, the results is an object
      withCategory = true;
      processedResults = cloneDeep(results);

      Object.keys(processedResults).forEach(key => {
        processedResults[key].results = processedResults[key].results.map(result => ({ title: result.title }));
      });
    }

    return (
      <SemanticSearch
        {...remainingProps}
        category={withCategory}
        loading={loading}
        value={value}
        results={processedResults}
        onSearchChange={event => this.onSearchChange(event)}
        onResultSelect={(event, data) => this.onResultSelect(event, data)}
      />
    );
  }
}

Search.propTypes = {
  value: PropTypes.string,
  minCharacters: PropTypes.number,
  asyncSearchCallback: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onResultSelect: PropTypes.func.isRequired,
  debounceOptions: PropTypes.shape({
    timeout: PropTypes.number.isRequired,
    leading: PropTypes.bool.isRequired,
    trailing: PropTypes.bool.isRequired,
  }),
};

Search.defaultProps = {
  minCharacters: 1,
  debounceOptions: {
    timeout: 200,
    leading: false,
    trailing: true,
  },
};

export default Search;
